import { createFileRoute, notFound } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { getGoldRates } from "@/lib/gold-prices.functions";
import { getSilverRates } from "@/lib/silver-prices.functions";
import { getGoldHistory } from "@/lib/gold-history.functions";
import { findEmbed } from "@/lib/embed/registry";
import { GoldEmbed, SilverEmbed } from "@/components/embed/MetalEmbed";
import { AgeCalcEmbed, BmiEmbed, GoldCalcEmbed, PregnancyEmbed, ZakatEmbed } from "@/components/embed/CalcEmbeds";
import { computeCityPrices } from "@/lib/gold-data";
import { computeSilverPrices } from "@/lib/silver-data";

const goldQO = queryOptions({ queryKey: ["embed-gold"], queryFn: () => getGoldRates(), staleTime: 10 * 60 * 1000 });
const silverQO = queryOptions({ queryKey: ["embed-silver"], queryFn: () => getSilverRates(), staleTime: 10 * 60 * 1000 });
const historyQO = (country: "pakistan" | "india") =>
  queryOptions({
    queryKey: ["embed-history", country],
    queryFn: () => getGoldHistory({ data: { country } }),
    staleTime: 60 * 60 * 1000,
  });

export const Route = createFileRoute("/embed/page/$id")({
  parseParams: ({ id }) => {
    if (!findEmbed(id)) throw notFound();
    return { id };
  },
  loader: async ({ context, params }) => {
    const e = findEmbed(params.id)!;
    if (e.category === "silver") await context.queryClient.ensureQueryData(silverQO);
    else await context.queryClient.ensureQueryData(goldQO);
    if (e.id === "zakat-calculator") void context.queryClient.prefetchQuery(silverQO);
    if (e.id === "gold-pakistan") void context.queryClient.prefetchQuery(historyQO("pakistan"));
    if (e.id === "gold-india") void context.queryClient.prefetchQuery(historyQO("india"));
  },
  head: ({ params }) => {
    const e = findEmbed(params.id);
    return {
      meta: [
        { title: e ? `${e.name} — DrHint Embed` : "DrHint Embed" },
        { name: "robots", content: "noindex,follow" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        { httpEquiv: "Content-Security-Policy", content: "frame-ancestors *;" },
      ],
    };
  },
  component: EmbedRoute,
  notFoundComponent: () => <div style={{ padding: 16, fontFamily: "system-ui" }}>Widget not found</div>,
  errorComponent: () => <div style={{ padding: 16, fontFamily: "system-ui", color: "#888" }}>Widget unavailable</div>,
});

function EmbedRoute() {
  return (
    <Suspense fallback={<div style={{ padding: 16, color: "#888", fontFamily: "system-ui" }}>Loading live data…</div>}>
      <EmbedInner />
    </Suspense>
  );
}

function EmbedInner() {
  const { id } = Route.useParams();
  const e = findEmbed(id)!;

  if (e.category === "silver") return <SilverEmbedLoader id={id} />;
  return <GoldSideEmbed id={id} />;
}

function SilverEmbedLoader({ id }: { id: string }) {
  const e = findEmbed(id)!;
  const { data } = useSuspenseQuery(silverQO);
  const country = id === "silver-india" ? "india" : "pakistan";
  const fx = country === "pakistan" ? data.pkrPerUsd : data.inrPerUsd;
  return <SilverEmbed country={country} spotUsdPerOz={data.spotUsdPerOz} fx={fx} target={e.target} />;
}

function GoldSideEmbed({ id }: { id: string }) {
  const e = findEmbed(id)!;
  const { data } = useSuspenseQuery(goldQO);

  switch (id) {
    case "gold-pakistan":
    case "gold-india": {
      const country = id === "gold-india" ? "india" : "pakistan";
      return <GoldWithHistory country={country} spot={data.spotUsdPerOz} fx={country === "pakistan" ? data.pkrPerUsd : data.inrPerUsd} target={e.target} />;
    }
    case "gold-calculator-pakistan":
    case "gold-calculator-india": {
      const country = id.endsWith("india") ? "india" : "pakistan";
      return (
        <GoldCalcEmbed
          country={country}
          spotUsdPerOz={data.spotUsdPerOz}
          fx={country === "pakistan" ? data.pkrPerUsd : data.inrPerUsd}
          target={e.target}
        />
      );
    }
    case "age-calculator":
      return <AgeCalcEmbed target={e.target} />;
    case "pregnancy-calculator":
      return <PregnancyEmbed target={e.target} />;
    case "bmi-calculator":
      return <BmiEmbed target={e.target} />;
    case "zakat-calculator":
    default:
      return <ZakatLoader target={e.target} spot={data.spotUsdPerOz} fx={data.pkrPerUsd} />;
  }
}

function GoldWithHistory({
  country,
  spot,
  fx,
  target,
}: {
  country: "pakistan" | "india";
  spot: number;
  fx: number;
  target: string;
}) {
  const { data: hist } = useSuspenseQuery(historyQO(country));
  const points = hist.points.map((p) => p.price24k).filter((n) => Number.isFinite(n) && n > 0);
  return <GoldEmbed country={country} spotUsdPerOz={spot} fx={fx} history={points} target={target} />;
}

function ZakatLoader({ target, spot, fx }: { target: string; spot: number; fx: number }) {
  const { data: silver } = useSuspenseQuery(silverQO);
  const gold = computeCityPrices({ spotUsdPerOz: spot, fxRate: fx, country: "pakistan", cityPremium: 1 });
  const s = computeSilverPrices({ spotUsdPerOz: silver.spotUsdPerOz, fxRate: silver.pkrPerUsd, country: "pakistan" });
  return (
    <ZakatEmbed goldPerGram24={gold.perGram24} silverPerGram={s.perGram} currency="PKR" target={target} />
  );
}
